import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, ViewChild } from "@angular/core";
import {
    ConfirmDialogModel,
    NmsWizardComponent,
    NmsWizardStepMapData,
    ProcessChangesModel
} from "@nms-angular-toolkit/nms-wizard";
import { DialogService } from "@nms-ng2/app/shared/services/dialog/dialog.service";
import {
    ANGULARJS_SCOPE,
    ANGULARJS_TRANSLATE,
    NMS_STATES,
    STATE
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { Subscription } from "rxjs";
import { CircuitService } from "../services/circuit.service";
import { DwdmCircuitWizardUtilsService } from "./utils/dwdm-circuit-wizard-utils.service";
import { CircuitDiagram } from "../diagram/models/diagram.models";

/**
 * Componente DwdmCircuitWizard.
 *
 * Steps:
 *     1. Ends
 *     2. Transponders
 *     3. Intermediates
 *     4. Links
 *     5. Amplifiers
 *     6. OscManagement
 *     7. General
 */
@Component({
    selector: "dwdm-circuit-wizard",
    templateUrl: "./dwdm-circuit-wizard.component.html",
    styleUrls: ["./dwdm-circuit-wizard.component.scss"]
})
export class DwdmCircuitWizardComponent implements AfterViewInit, OnDestroy {
    subscriptions: Array<Subscription> = [];
    @ViewChild(NmsWizardComponent)
    nmsWizard: NmsWizardComponent;
    wizardStepsCacheMap: NmsWizardStepMapData = {};
    isLoading: boolean = false;
    circuitWizardTitle: string = "dwdm.circuit.wizard.add";
    initialCircuitHash: number;
    discardChangesConfirmMessage: ConfirmDialogModel;

    circuitDiagram: CircuitDiagram;
    circuitDiagramHide: boolean = false;

    constructor(@Inject(STATE) private readonly $state: any,
                @Inject(NMS_STATES) private readonly nmsStates: any,
                @Inject(ANGULARJS_SCOPE) private readonly $scope: any,
                private readonly changeDetectorRef: ChangeDetectorRef,
                private readonly dialogService: DialogService,
                private readonly circuitService: CircuitService,
                private readonly dwdmCircuitWizardUtilsService: DwdmCircuitWizardUtilsService,
                @Inject(ANGULARJS_TRANSLATE) private readonly translate: any) {
        const circuitId = this.$scope.$parent.$resolve.$stateParams.circuitId;
        if (circuitId) {
            this.isLoading = true;
            this.loadCircuitData(circuitId);
            this.circuitWizardTitle = "dwdm.circuit.wizard.edit";
        }
        this.discardChangesConfirmMessage = {
            title: this.translate.instant("dwdm.circuit.wizard.discard.next.steps.title"),
            message: this.translate.instant("dwdm.circuit.wizard.discard.next.steps")
        };
    }

    private readonly dwdmLockControl = new BroadcastChannel("dwdm-lock-control");
    private readonly tabId = `dwdm-circuit-wizard-${Date.now()}`;
    private readonly onChannelBroadcastMessage = ({ data: { eventType, id } }) => {
        if (this.tabId !== id) {
            this.dwdmLockControl.postMessage({ eventType: "CLOSE", id });
        } else if (eventType === "CLOSE" && this.tabId === id) {
            this.dwdmLockControl.close();
            setTimeout(() => {
                this.dialogService.openDialog({ isConfirm: false, translateKey: "dwdm.circuit.wizard.locked.error.message" })
                    .then(() => this.$state.go(this.nmsStates.dwdmCircuits.list))
                    .catch(() => this.$state.go(this.nmsStates.dwdmCircuits.list));
            });
        }
    };

    async loadCircuitData(circuitId: number) {
        const data = await this.circuitService.loadCircuitById(circuitId).toPromise();
        this.wizardStepsCacheMap = await this.dwdmCircuitWizardUtilsService.fillWizardSteps(data, circuitId);
        this.initialCircuitHash = this.dwdmCircuitWizardUtilsService.calculateCacheHash(this.wizardStepsCacheMap);
        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.registerTabInBroadcastChannel();

        const updateDiagramSubscription = this.dwdmCircuitWizardUtilsService.updateDiagram.subscribe(
            (circuitDiagram: CircuitDiagram) => {
                this.circuitDiagram = circuitDiagram;
            }
        );

        const hideDiagrmaSubscription = this.dwdmCircuitWizardUtilsService.hideDiagram.subscribe((hide: boolean) => {
            this.circuitDiagramHide = hide;
        });

        this.subscriptions.push(updateDiagramSubscription, hideDiagrmaSubscription);
    }

    ngAfterViewChecked(): void {
        this.changeDetectorRef.detectChanges();
    }

    ngOnDestroy() {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.dwdmLockControl.close();
    }

    cancel(): void {
        this.confirmCancel();
    }

    verifyChangesAndInvalidate(processChangesModel: ProcessChangesModel) {
        this.nmsWizard.verifyChangesAndInvalidate(processChangesModel);
    }

    private registerTabInBroadcastChannel() {
        this.dwdmLockControl.postMessage({ eventType: "HELLO", id: this.tabId });
        this.dwdmLockControl.onmessage = this.onChannelBroadcastMessage;
    }

    private confirmCancel(): void {
        const confirmMessage = "dwdm.circuit.wizard.cancel.confirm";

        const confirmationModalFn    = () => true;
        const confirmationCallbackFn = () => this.$state.go(this.nmsStates.dwdmCircuits.list);
        const cancelCallbackFn       = () => true;

        this.dialogService.confirm(confirmationModalFn, confirmMessage, confirmationCallbackFn, cancelCallbackFn);
    }

}
